<template>
  <header class="academy-header">
    <div class="academy-header__logo">
      <img src="~/assets/images/logo.svg" alt="Littlelives" />
    </div>
    <div v-if="!$parent.loading" class="flex justify-center w-full">
      <div class="flex justify-between items-center max-w-[1600px] xl:w-[944px] min-w-[944px]">
        <div class="content-left">
          <SchoolHQSelect
            v-if="shouldDisplay"
            :isFirstLevel="false"
            :schoolTreeHasChildren="schoolTreeHasChildren"
            :value="schoolProfile"
            :onSelectSchool="handleSelectSchool"
          ></SchoolHQSelect>
        </div>
        <div class="content-right">
          <div class="academy-header__user academy-header-user flex items-center">
            <BaseDropdown class="h-[32px]" :options="languageOptions" :onClickOption="handleChooseLanguage">
              <template #dropdown-trigger>
                <BaseIconSVG clickable class="text-grey-5" name="language" />
              </template>
            </BaseDropdown>
            <b-dropdown aria-role="menu" class="mx-3 user-cta" position="is-bottom-left" :mobileModal="false">
              <template #trigger>
                <Avatar class="academy-header-user__profile-btn" :src="userImageUrl" />
              </template>
              <b-dropdown-item aria-role="menu-item" @click="$router.push('/personal-profile/basic-info')">{{
                $t('account')
              }}</b-dropdown-item>
              <b-dropdown-item class="is-border-bottom" aria-role="menu-item"></b-dropdown-item>
              <b-dropdown-item v-if="isUsingInternalToken" aria-role="menu-item" @click="handleLogout">{{ $t('sign-out') }}</b-dropdown-item>
              <b-dropdown-item v-if="!isUsingInternalToken" aria-role="menu-item" @click="handleGoBack">{{ $t('go-back') }}</b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import BaseIconSVG from '../v2/DataDisplay/BaseIconSVG.vue'
import BaseDropdown from '../v2/Inputs/BaseDropdown.vue'
import { setCookieLanguage } from '~/utils/cookie'
import defaultUserImage from '~/assets/images/default-avatar.png'
import HomeFilterOptions from '~/components/header/HomeFilterOptions'
import SchoolHQSelect from '~/components/v2/Inputs/SchoolHQSelect'

export default {
  name: 'AcademyHeader',
  components: {
    SchoolHQSelect,
    BaseIconSVG,
    BaseDropdown,
  },
  data() {
    return {
      currentLocale: this.$cookie.get('locale'),
      loading: true,
    }
  },
  computed: {
    ...mapGetters({
      userProfile: 'profile/userProfile',
      schoolTree: 'school/schoolTree',
      schoolTreeHasChildren: 'school/schoolTreeHasChildren',
      schoolProfile: 'school/schoolProfile',
      schoolOrgId: 'school/schoolOrgId',
    }),
    languageOptions() {
      return [
        {
          label: this.$t('English'),
          value: 'en',
        },
        {
          label: this.$t('Chinese'),
          value: 'zh',
        },
        {
          label: this.$t('Indo'),
          value: 'id',
        },
      ]
    },
    shouldDisplay() {
      const { name, path } = this.$route
      return (
        [
          'academy',
          'academy-my-activity',
          'school-profile-basic',
          'staff-all',
          'staff-configuration',
          'performance-my-appraisal',
          'performance-appraisal-given',
          'performance-all-appraisal',
          'performance-overview-frameworks',
          'performance-assign',
          'performance-appraisal-period',
          'performance-review-process',
          'training',
          'training-playlists',
          'settings-permissions',
          'settings-billing',
          'admin',
          'admin-trainers',
          'reflection',
        ].includes(name) || path.includes('/personal-profile')
      )
    },
    languages() {
      return [
        { name: '🇺🇸 English', locale: 'en' },
        { name: '🇻🇳 Vietnamese', locale: 'vn' },
        { name: '🇨🇳 Chinese', locale: 'zh' },
      ]
    },
    currentLocaleDisplay() {
      return this.languages.find((lang) => lang.locale === this.currentLocale) || {}
    },
    userImageUrl() {
      return this.$get(this.userProfile, 'user.personalProfiles[0].media.url', defaultUserImage)
    },
    isUsingInternalToken() {
      const internalToken = this.$cookie.get('laAccessToken')
      return !!internalToken
    },
    middleComponents() {
      const filterComponents = {
        academy: {
          component: HomeFilterOptions,
          data: {},
        },
      }
      return filterComponents
    },
    activeMiddleComponent() {
      const { path } = this.$route
      if (path.startsWith('/academy')) {
        return this.middleComponents.academy
      }

      return { component: null, data: {} }
    },
  },
  methods: {
    ...mapActions({
      getSchoolProfile: 'school/FETCH_SCHOOL_PROFILE',
      getORG_KEYWORDS: 'organisation/FETCH_ORG_KEYWORDS',
      fetchOrgTitles: 'organisation/FETCH_ORG_TITLES',
    }),
    handleChooseLanguage(index) {
      this.handleSwitchLang(this.languageOptions[index].value)
    },
    async handleSelectSchool(school) {
      this.$cookie.set('schoolId', school.id)
      await this.getSchoolProfile(school)
      await this.getORG_KEYWORDS({ organisationId: this.schoolOrgId })
    },
    handleSwitchLang(value) {
      this.$i18n.locale = value
      switch (value) {
        case 'vn':
          this.$moment.locale('vi')
          moment.locale('vi')
          break
        case 'zh':
          this.$moment.locale('zh-cn')
          moment.locale('zh-cn')
          break
        case 'id':
          this.$moment.locale('id')
          moment.locale('id')
          break
        default:
          this.$moment.locale('en')
          moment.locale('en')
          break
      }
      setCookieLanguage(this.$cookie, value)
    },
    async handleLogout() {
      try {
        this.$cookie.remove('laAccessToken')
        this.$cookie.remove('academy_refresh_token')
        this.$cookie.remove('schoolId')
        await this.$apolloHelpers.onLogout()
        window.location.replace('/signin')
      } catch (error) {
        console.error('handleLogout', error)
      }
    },
    handleGoBack() {
      window.location.href = process.env.NUXT_ENV_LITTLELIVES_APP_ENDPOINT
    },
    // async getOrgTitles() {
    //   // TODO: Please refactor this code when you have more than 2 levels of layers in organisation structure
    //   const organisationId = this.schoolProfile?.parentId ?? this.schoolProfile?.id
    //   if (organisationId) {
    //     await this.fetchOrgTitles({
    //       organisationId,
    //     })
    //   }
    //   this.fetchOrgTitles({
    //     country: this.schoolProfile?.countryCode,
    //   })
    // },
  },
  // watch: {
  //   schoolProfile() {
  //     this.getOrgTitles()
  //   }
  // },
  // mounted() {
  //   this.getOrgTitles()
  // }
}
</script>

<style lang="scss">
.dropdown.user-cta {
  & > .dropdown-menu {
    width: 145px;
    min-width: 145px;

    > .dropdown-content {
      background-color: #7f7f7f;
    }
  }

  .dropdown-content > a.dropdown-item {
    color: #fff;
    font-family: Nunito Sans;
    text-transform: capitalize;
    font-size: $font-size-16px;

    &.is-border-bottom {
      border-bottom: 1px solid #9b9b9b;
      padding: 0;
      margin: 4px 0;
    }

    &:hover {
      background-color: #999;
    }
  }
}
</style>

<style lang="scss" scoped>
.select-languages-header {
  margin-right: 20px;
}

.academy-header {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 10px 24px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  background-color: white;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;

  .academy-header__logo {
    width: 240px;
    > img {
      max-height: 100%;
    }
  }

  .academy-header__quote {
    flex: 1;
    padding-right: 10px;
    padding-left: 58px;
    font-family: Nunito;
    font-size: 20px;
    font-weight: 600;
    line-height: 1;
    color: $color-theme-bright;

    @media screen and (max-width: 1023px) {
      font-size: 16px;
      padding-left: 0;
    }

    @media screen and (max-width: 540px) {
      font-size: 14px;
    }
  }
}

.academy-header-user__profile-btn {
  width: 32px !important;
  height: 32px !important;
  border-radius: 16px;
  cursor: pointer;

  &.is-empty {
    background-color: $color-grey-light;

    &:hover {
      background-color: $color-theme-bright;
    }
  }

  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  border: 1px solid $color-regular-grey;
}

.academy-header-user__button-logout {
  display: inline-block;
  margin-left: 20px;
  color: $color-bright-sky-blue;
  cursor: pointer;
  position: relative;
  top: 3px;
}

.academy-header__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  padding-right: 30px;

  .content-middle {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 0 30px;
  }

  .content-left {
    margin-left: 3.75rem;

    /deep/.collapse {
      margin-left: 64px;
      width: 304px;
    }
  }

  .content-right {
    .academy-header__user {
      display: flex;
      align-items: center;
    }
  }
}
</style>
